import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Play,
  ArrowRight,
  MapPin,
  Radio,
  Heart,
  Church,
  Calendar,
  BookOpen,
  Languages,
  Users,
  Sparkles,
  HandHeart,
  Cross,
  ChevronRight,
  Crown,
  Flame,
  Handshake,
  Globe2,
  HeartPulse,
  HeartHandshake,
  GraduationCap,
  Mic2,
  Radio as RadioIcon,
  Music2,
  Baby,
  UsersRound,
  BookOpenCheck,
  Coffee,
} from "lucide-react";
import ReviewsBlock from "../components/Reviews";
import {
  LOGO_LIFETALK_RADIO,
  LOGO_FATHERS_HOUSE,
  TAGLINE,
  FM_FREQUENCIES,
  CHURCH_NAME,
  CHURCH_TAGLINE,
  CHURCH_ADDRESS,
  CHURCH_SERVICE_TIME,
} from "../lib/siteConfig";

const HERO_SLIDES = [
  "https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/6r3tp3i9_IMG-20260430-WA0023.jpg",
  "https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/884kkpol_20260515_113126%20-%20Copy.jpg",
  "https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/xz2op469_1000175505.jpg",
  "https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/5ous1wwk_1000175472.jpg",
  "https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/nkxh5a59_1000175424.jpg",
];

/* -------------------------------------------------------------------------- */
/*  Cinematic fast-cycling hero background slideshow                          */
/* -------------------------------------------------------------------------- */
function HeroSlideshow() {
  const [active, setActive] = useState(0);

  // Preload all slides up-front so cross-fades have no flicker.
  useEffect(() => {
    HERO_SLIDES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const tick = setInterval(() => {
      setActive((i) => (i + 1) % HERO_SLIDES.length);
    }, 2600); // ~2.6s per slide
    return () => clearInterval(tick);
  }, []);

  return (
    <div
      data-testid="hero-slideshow"
      aria-hidden
      className="absolute inset-0 overflow-hidden"
    >
      {HERO_SLIDES.map((src, i) => (
        <div
          key={src}
          data-testid={`hero-slide-${i}`}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      ))}
      {/* Dark legibility overlay + brand red wash */}
      <div
        className="absolute inset-0 hero-vignette"
        style={{
          background:
            "radial-gradient(circle at 25% 25%, rgba(197,0,26,0.32), transparent 55%), linear-gradient(180deg, rgba(11,12,16,0.55) 0%, rgba(11,12,16,0.78) 50%, rgba(11,12,16,0.96) 88%, #0B0C10 100%)",
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tabbed identity content — Vision / Statement of Faith / Heart & Purpose   */
/* -------------------------------------------------------------------------- */
const IDENTITY_TABS = [
  { id: "vision", label: "Vision & Mission", Icon: Sparkles },
  { id: "faith", label: "Statement of Faith", Icon: Cross },
  { id: "heart", label: "Our Heart & Purpose", Icon: HandHeart },
];

const MISSION_BULLETS = [
  {
    Icon: Handshake,
    title: "Partnership in the Great Commission",
    body:
      "To work together with local churches, like-minded ministries, community organisations, and businesses to fulfil the Great Commission and expand the reach of the Gospel.",
  },
  {
    Icon: RadioIcon,
    title: "Broadcasting Eternal Hope",
    body:
      "To share the message of Jesus Christ through every available media platform — FM radio, online streaming, and digital outreach — reaching people of all cultures, languages, and backgrounds.",
  },
  {
    Icon: HeartPulse,
    title: "Strengthening Communities",
    body:
      "To encourage, support, and strengthen individuals and families through faith-based programs, prayer, teaching, and community-focused initiatives that bring hope and healing.",
  },
  {
    Icon: HandHeart,
    title: "Demonstrating God's Love",
    body:
      "To consistently reflect the love, grace, and truth of God through the unchanging Word of God in an ever-changing world, inspiring people to grow in faith and experience the transforming power of Jesus Christ.",
  },
  {
    Icon: Languages,
    title: "Reaching Every Language and Generation",
    body:
      "To raise up multicultural voices and Christ-centred content that connects with people of all ages and ethnicities, ensuring the Gospel message remains accessible, relevant, and impactful for future generations.",
  },
];

const FAITH_BLOCKS = [
  {
    Icon: BookOpen,
    title: "The Holy Bible",
    body:
      "We believe the Bible is the inspired and living Word of God, written through men led by the Holy Spirit. God's Word is eternal, trustworthy, and powerful to transform lives, strengthen faith, and bring hope to every generation.",
    verse:
      "“For whatever was written in former days was written for our instruction… that we might hold fast to hope.” — Romans 15:4",
    image:
      "https://images.unsplash.com/photo-1504052434569-70ad5836ab65?auto=format&fit=crop&w=1600&q=70",
  },
  {
    Icon: Crown,
    title: "The One True God",
    body:
      "We believe in one true God — Father, Son, and Holy Spirit — three Persons in one divine nature, equal in power, glory, and authority. God is holy, loving, faithful, and worthy of all worship and praise.",
    image:
      "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?auto=format&fit=crop&w=1600&q=70",
  },
  {
    Icon: Cross,
    title: "Jesus Christ",
    body:
      "We believe Jesus Christ is the Son of God, born of a virgin, fully God and fully man. He lived a sinless life, died on the cross for our sins, rose again in victory, and is coming back in glory. Salvation and eternal life are found only through Him.",
    verse: "“This is My beloved Son, in Whom I delight!” — Matthew 3:17",
    image:
      "https://images.unsplash.com/photo-1601925225929-26416e1d6e95?auto=format&fit=crop&w=1600&q=70",
  },
  {
    Icon: Flame,
    title: "The Holy Spirit",
    body:
      "We believe the Holy Spirit lives within believers, guiding, strengthening, comforting, and empowering us to live for Christ. The gifts and fruit of the Holy Spirit are active today, bringing healing, encouragement, and spiritual growth to the Church.",
    image:
      "https://images.unsplash.com/photo-1517414204284-fb7e1b1fdfae?auto=format&fit=crop&w=1600&q=70",
  },
];

/* -------------------------------------------------------------------------- */
/*  Sub-ministry datasets                                                     */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */
function FmTicker() {
  // Duplicate the array so the marquee loop is seamless.
  const items = [...FM_FREQUENCIES, ...FM_FREQUENCIES];
  return (
    <div
      data-testid="fm-ticker"
      className="relative w-full overflow-hidden border-y border-white/10 bg-gradient-to-r from-[#1F2833] via-[#0B0C10] to-[#1F2833]"
    >
      {/* ambient red glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(197,0,26,0.18), transparent 60%)",
        }}
      />
      <div className="relative flex items-center gap-8 sm:gap-10 py-5 animate-[scroll_40s_linear_infinite] whitespace-nowrap will-change-transform">
        {items.map((it, i) => (
          <span
            key={i}
            className="group inline-flex items-center gap-3 text-sm sm:text-base"
          >
            {/* Tiny per-frequency equalizer */}
            <span className="eq-mini" aria-hidden>
              <span />
              <span />
              <span />
              <span />
            </span>
            <span className="font-display font-extrabold tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-[#FF3B30] via-[#E31C3D] to-[#C5001A] drop-shadow-[0_0_12px_rgba(255,59,48,0.35)]">
              {it.freq}
            </span>
            <span className="text-white/85 font-medium tracking-wide">
              {it.region}
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#C5001A]/60" />
          </span>
        ))}
      </div>
      <style>{`
        @keyframes scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

function IdentityTabs() {
  const [active, setActive] = useState("vision");
  const sectionRef = useRef(null);

  const handleTabChange = (id) => {
    setActive(id);
    // Snap the section's top into view after the next paint so the user
    // sees the new tab content from its absolute top.
    requestAnimationFrame(() => {
      const top = sectionRef.current?.getBoundingClientRect().top;
      if (top != null) {
        try {
          window.scrollTo({ top: window.scrollY + top - 80, behavior: "instant" });
        } catch {
          window.scrollTo(0, window.scrollY + top - 80);
        }
      }
    });
  };

  return (
    <section
      ref={sectionRef}
      data-testid="identity-tabs"
      className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24 border-t border-white/5"
    >
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
            Who we are
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight max-w-2xl leading-[1.05]">
            Anchored in Scripture, reaching every nation.
          </h2>
        </div>
        <p className="text-[#C5D1EB] text-sm sm:text-base max-w-md leading-relaxed">
          The vision, faith, and heart that shape every broadcast, every
          gathering, and every word we share.
        </p>
      </div>

      {/* Tab buttons */}
      <div role="tablist" className="flex flex-wrap gap-2 mb-8">
        {IDENTITY_TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={active === t.id}
            data-testid={`identity-tab-${t.id}`}
            onClick={() => handleTabChange(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm border transition-all duration-200 backdrop-blur-md ${
              active === t.id
                ? "bg-[#C5001A] border-[#C5001A] text-white shadow-[0_8px_30px_-8px_rgba(197,0,26,0.6)]"
                : "bg-white/[0.04] border-white/10 text-[#C5D1EB] hover:text-white hover:border-white/30 hover:bg-white/[0.08]"
            }`}
          >
            <t.Icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {active === "vision" && (
        <div data-testid="tab-content-vision" className="grid lg:grid-cols-12 gap-6">
          <article
            data-testid="vision-card"
            className="relative lg:col-span-7 overflow-hidden border border-white/10 min-h-[460px]"
          >
            <div
              aria-hidden
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage:
                  "url('https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/yn9qcxhp_Our%20vision.jpg')",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(11,12,16,0.55) 0%, rgba(11,12,16,0.82) 55%, rgba(11,12,16,0.95) 100%)",
              }}
            />
            <div className="relative p-8 sm:p-10">
              <div className="flex items-center gap-3">
                <Sparkles size={18} className="text-[#C5001A]" />
                <h3 className="font-display text-2xl text-white tracking-tight">
                  Our Vision
                </h3>
              </div>
              <p className="mt-5 text-[#C5D1EB] leading-relaxed text-[15px]">
                To proclaim the Gospel of Jesus Christ through radio and digital
                media, bringing the message of God's love, saving faith, and
                eternal hope to communities across New Zealand and beyond. Life
                Talk exists to reach the unreached — especially Hindi-speaking and
                South Asian communities including people from India, Nepal,
                Bangladesh, Pakistan, and Fijian-Indian backgrounds — while also
                embracing and serving multi-ethnic communities from every nation
                and culture.
              </p>
              <p className="mt-4 text-[#C5D1EB] leading-relaxed text-[15px]">
                Our vision is to see lives transformed by the power of God's
                Word, families strengthened, and hearts united in Christ through
                media ministry, so that the Gospel may be heard in every language
                with love, compassion, and truth — producing lasting spiritual
                fruit for the Kingdom of God.
              </p>
              <blockquote className="mt-7 border-l-2 border-[#C5001A] pl-5 py-1 text-white italic text-[15px] leading-relaxed">
                “For God so greatly loved and dearly prized the world that He
                [even] gave up His only begotten (unique) Son, so that whoever
                believes in Him shall not perish (come to destruction, be lost)
                but have eternal (everlasting) life.”
                <footer className="not-italic mt-2 text-[11px] tracking-[0.25em] uppercase text-[#C5001A]">
                  John 3:16 · Amplified Bible
                </footer>
              </blockquote>
            </div>
          </article>

          <aside
            data-testid="mission-card"
            className="relative lg:col-span-5 overflow-hidden border border-white/10 min-h-[460px]"
          >
            <div
              aria-hidden
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage:
                  "url('https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/pav7becn_Our%20Mission.jpg')",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(11,12,16,0.6) 0%, rgba(11,12,16,0.85) 55%, rgba(11,12,16,0.95) 100%)",
              }}
            />
            <div className="relative p-8 sm:p-10">
              <div className="flex items-center gap-3">
                <HandHeart size={18} className="text-[#C5001A]" />
                <h3 className="font-display text-2xl text-white tracking-tight">
                  Our Mission
                </h3>
              </div>
              <ol className="mt-6 space-y-5">
                {MISSION_BULLETS.map((m) => (
                  <li key={m.title} className="flex gap-4">
                    <span className="shrink-0 w-9 h-9 border border-[#C5001A]/60 bg-[#C5001A]/15 text-[#C5001A] flex items-center justify-center">
                      <m.Icon size={15} />
                    </span>
                    <div>
                      <p className="text-white font-display text-sm tracking-tight">
                        {m.title}
                      </p>
                      <p className="mt-1 text-[#C5D1EB] text-[13px] leading-relaxed">
                        {m.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      )}

      {active === "faith" && (
        <div
          data-testid="tab-content-faith"
          className="grid md:grid-cols-2 gap-5"
        >
          {FAITH_BLOCKS.map((b) => (
            <article
              key={b.title}
              data-testid={`faith-card-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="relative overflow-hidden border border-white/10 hover:border-white/25 transition-colors min-h-[340px]"
            >
              {b.image && (
                <div
                  aria-hidden
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url('${b.image}')` }}
                />
              )}
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(11,12,16,0.6) 0%, rgba(11,12,16,0.85) 55%, rgba(11,12,16,0.95) 100%)",
                }}
              />
              <div className="relative p-7 sm:p-8 h-full flex flex-col">
                <div className="flex items-center gap-3">
                  <span className="inline-flex w-10 h-10 bg-[#C5001A]/15 border border-[#C5001A]/60 text-[#C5001A] items-center justify-center">
                    <b.Icon size={17} />
                  </span>
                  <h3 className="font-display text-xl text-white tracking-tight">
                    {b.title}
                  </h3>
                </div>
                <p className="mt-4 text-[#C5D1EB] text-[14px] leading-relaxed">
                  {b.body}
                </p>
                {b.verse && (
                  <p className="mt-4 text-[13px] italic text-white/90 border-l-2 border-[#C5001A] pl-4">
                    {b.verse}
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {active === "heart" && (
        <div
          data-testid="tab-content-heart"
          className="relative overflow-hidden border border-white/10 min-h-[420px]"
        >
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/ingf2dd2_Our%20Heart%20%26%20Purpose.jpg')",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(11,12,16,0.55) 0%, rgba(11,12,16,0.82) 60%, rgba(11,12,16,0.96) 100%)",
            }}
          />
          <div
            aria-hidden
            className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#C5001A] opacity-25 blur-3xl"
          />
          <div className="relative p-8 sm:p-12">
            <div className="flex items-center gap-3">
              <HandHeart size={22} className="text-[#C5001A]" />
              <h3 className="font-display text-2xl text-white tracking-tight">
                Our Heart & Purpose
              </h3>
            </div>
            <p className="mt-6 text-white/90 text-base sm:text-lg leading-relaxed max-w-4xl">
              Our heart is to see lives transformed by the love and saving grace
              of Jesus Christ. We are committed to sharing the Gospel through
              worship, prayer, teaching, media ministry, and compassionate
              outreach — bringing hope to families, strengthening faith, and
              helping people from every culture encounter the living God. Our
              desire is to glorify Jesus Christ and see souls saved, lives
              restored, and communities touched by His everlasting love.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function FathersHouseBlock() {
  return (
    <section
      data-testid="fathers-house-block"
      className="relative overflow-hidden border-t border-white/5 bg-[#0B0C10]"
    >
      {/* Ambient red glow — dark-room studio atmosphere */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-1/4 w-[40rem] h-[40rem] rounded-full opacity-25 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 -bottom-40 w-[36rem] h-[36rem] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #E31C3D 0%, transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-6 items-stretch">
          {/* Left: identity */}
          <div className="lg:col-span-5 bg-white/[0.04] backdrop-blur-md border border-white/10 p-8 sm:p-10 flex flex-col">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
              Sister Ministry · Local Assembly
            </span>
            <div className="mt-6 flex items-center gap-4">
              <span className="flex items-center justify-center w-16 h-16 bg-white p-2 ring-1 ring-[#C5D1EB]/20">
                <img
                  src={LOGO_FATHERS_HOUSE}
                  alt={CHURCH_NAME}
                  className="w-full h-full object-contain"
                />
              </span>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight leading-tight">
                  Father's House
                </h2>
                <p className="text-[12px] tracking-[0.22em] uppercase text-[#C5D1EB]/70 mt-1">
                  {CHURCH_TAGLINE}
                </p>
              </div>
            </div>
            <p className="mt-7 text-[#C5D1EB] text-[15px] leading-relaxed">
              A Spirit-filled Indian church family gathering weekly in Gate Pa,
              Tauranga — a house of prayer for all nations, serving Indian and
              multi-ethnic believers across the Bay of Plenty.
            </p>

            <div className="mt-auto pt-8 flex flex-wrap items-center gap-4 text-sm text-[#C5D1EB]">
              <span className="flex items-center gap-2">
                <MapPin size={14} className="text-[#C5001A]" /> {CHURCH_ADDRESS}
              </span>
            </div>
            <Link
              to="/church"
              data-testid="church-cta"
              className="mt-6 inline-flex items-center justify-center gap-2 border border-white/20 hover:bg-[#0B0C10] hover:border-[#C5001A] text-[#C5D1EB] hover:text-white px-5 py-3 text-sm transition-colors w-full sm:w-auto"
            >
              Visit the church <ArrowRight size={14} />
            </Link>
          </div>

          {/* Right: service banner with red ambient glow */}
          <div className="lg:col-span-7 relative bg-white/[0.04] backdrop-blur-md border border-white/10 overflow-hidden flex flex-col justify-between p-8 sm:p-10">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-20 -top-20 w-80 h-80 rounded-full opacity-35 blur-[80px]"
              style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -left-12 -bottom-16 w-64 h-64 rounded-full opacity-25 blur-[80px]"
              style={{ background: "radial-gradient(circle, #E31C3D, transparent 70%)" }}
            />
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#C5001A]/15 text-[#E31C3D] border border-[#C5001A]/40 text-[10px] tracking-[0.25em] uppercase backdrop-blur-md">
                <Calendar size={11} /> Weekly Gathering
              </span>
              <h3 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
                Sunday · 4:00 PM <span className="text-[#C5001A]">—</span> 6:00 PM
              </h3>
              <p className="mt-5 text-[#C5D1EB] text-base leading-relaxed max-w-md">
                Worship, the Word, and family fellowship in the heart of the
                Bay of Plenty. Every nation, every generation welcome.
              </p>
            </div>

            <div className="relative mt-10 max-w-md">
              <Link
                to="/church"
                className="inline-flex w-full items-center justify-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-5 py-3 text-sm transition-colors shadow-[0_10px_30px_-10px_rgba(197,0,26,0.7)]"
              >
                <Church size={14} /> Directions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Founders + Trustees + Life Talk Radio Station block                        */
/* -------------------------------------------------------------------------- */
function FoundersBlock() {
  return (
    <section
      data-testid="founders-block"
      className="relative overflow-hidden border-t border-white/5"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-32 w-[40rem] h-[40rem] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
              Founders
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight max-w-2xl leading-[1.05]">
              Pastors Robinson & Esther Martin
            </h2>
          </div>
          <p className="text-[#C5D1EB] text-sm sm:text-base max-w-md leading-relaxed">
            Founders and senior pastors carrying the vision of Life Talk Trust —
            equal parts shepherds of the Tauranga assembly and voices of the
            24/7 broadcast.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Founder portrait */}
          <div
            data-testid="founder-photo"
            className="lg:col-span-5 relative bg-white/[0.03] backdrop-blur-md border border-white/10 overflow-hidden min-h-[420px]"
          >
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 30% 20%, rgba(197,0,26,0.28), transparent 60%), radial-gradient(circle at 70% 80%, rgba(227,28,61,0.22), transparent 60%)",
              }}
            />
            <img
              src="https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/plmaujm8_Founder%20image.png"
              alt="Pastors Robinson & Esther Martin — founders of Life Talk Trust and senior pastors of Father's House Indian Church Tauranga"
              loading="lazy"
              className="relative w-full h-full object-cover object-top"
            />
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, transparent 0%, rgba(11,12,16,0.7) 100%)",
              }}
            />
            <div data-testid="founder-caption" className="absolute left-5 bottom-4 z-10">
              <p className="text-[10px] tracking-[0.3em] uppercase">
                Founders
              </p>
              <p className="font-display tracking-tight text-sm">
                Pastors Robinson & Esther Martin
              </p>
            </div>
          </div>

          {/* Founder bio card with ambient crimson patches */}
          <article className="lg:col-span-7 relative bg-white/[0.04] backdrop-blur-md border border-white/10 p-8 sm:p-10 overflow-hidden">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-20 -top-24 w-80 h-80 rounded-full opacity-30 blur-[100px]"
              style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -left-16 -bottom-20 w-72 h-72 rounded-full opacity-25 blur-[100px]"
              style={{ background: "radial-gradient(circle, #E31C3D, transparent 70%)" }}
            />
            <div className="relative">
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
                Founders' Message
              </span>
              <h3 className="mt-4 font-display text-2xl sm:text-3xl text-white tracking-tight">
                A heart for Aotearoa and the nations.
              </h3>
              <p className="mt-5 text-[#C5D1EB] text-[15px] leading-relaxed">
                Raised in faithful pastoral families in India, Pastors Robinson
                and Esther Martin have dedicated their lives to serving Jesus
                Christ and sharing His unfailing love. Together with their
                children, Keziah and Jedidiah, they are united by a deep
                commitment to seeing lives transformed through the power and
                truth of God's Word.
              </p>
              <p className="mt-4 text-[#C5D1EB] text-[15px] leading-relaxed">
                As the Founder, Director, and Trustee of LifeTalk Trust — New
                Zealand's first multi-ethnic gospel radio station — Robinson,
                partnered closely by Esther's active ministry outreach, is
                passionate about advancing the Gospel. Together, they carry a
                shared vision to reach diverse communities across New Zealand
                with the message of hope, faith, and salvation found in Jesus
                Christ. Through this media platform and direct community
                engagement, they seek to inspire, encourage, and equip people
                on their spiritual journeys.
              </p>
              <p className="mt-4 text-[#C5D1EB] text-[15px] leading-relaxed">
                Their united prayer is that God's Word will bring profound
                hope, encouragement, and transformation to people throughout
                Aotearoa New Zealand. They pray that every listener will
                encounter God's love, experience His transforming power, and
                grow in a deeper, lasting relationship with Jesus. Thank you
                for tuning in, and may God richly bless you.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function TrusteesBlock() {
  const [trustees, setTrustees] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/trustees/published`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled) return;
        setTrustees(Array.isArray(data) ? data : []);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loaded && trustees.length === 0) return null;

  return (
    <section
      data-testid="trustees-block"
      className="border-t border-white/5 bg-[#0B0C10]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
              The Board
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight max-w-2xl leading-[1.05]">
              Life Talk Trustees
            </h2>
          </div>
          <p className="text-[#C5D1EB] text-sm sm:text-base max-w-md leading-relaxed">
            Our trustees provide oversight, accountability, and prayerful
            governance for Life Talk Trust.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {trustees.map((t, i) => (
            <article
              key={t.id || i}
              data-testid={`trustee-card-${i}`}
              className="relative bg-white/[0.04] backdrop-blur-md border border-white/10 overflow-hidden flex flex-col"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-10 w-48 h-48 rounded-full opacity-25 blur-[80px]"
                style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
              />
              {t.image_url && (
                <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#1F2833]">
                  <img
                    src={t.image_url}
                    alt={t.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-24 pointer-events-none trustee-photo-fade"
                  />
                </div>
              )}
              <div className="relative p-7 flex-1 flex flex-col">
                <p className="text-[11px] tracking-[0.25em] uppercase text-[#C5D1EB]/70">
                  {t.role || "Trustee"}
                </p>
                <p className="mt-1 font-display text-xl text-white tracking-tight">
                  {t.name}
                </p>
                <p className="mt-4 text-sm text-[#C5D1EB] leading-relaxed whitespace-pre-line">
                  {t.bio}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function RadioStationBlock() {
  return (
    <section
      data-testid="radio-station-block"
      className="relative overflow-hidden border-t border-white/5"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-1/4 w-[40rem] h-[40rem] rounded-full opacity-25 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-6 items-stretch">
          {/* Left identity */}
          <div className="lg:col-span-5 bg-white/[0.04] backdrop-blur-md border border-white/10 p-8 sm:p-10 flex flex-col">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
              The Broadcast
            </span>
            <div className="mt-6 flex items-center gap-4">
              <span className="flex items-center justify-center w-16 h-16 bg-white p-2 ring-1 ring-[#C5D1EB]/20">
                <img
                  src={LOGO_LIFETALK_RADIO}
                  alt="Life Talk Radio"
                  className="w-full h-full object-contain"
                />
              </span>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight leading-tight">
                  Life Talk Radio Station
                </h2>
                <p className="text-[12px] tracking-[0.22em] uppercase text-[#C5D1EB]/70 mt-1">
                  24 / 7 · Indian & multi-ethnic gospel
                </p>
              </div>
            </div>
            <p className="mt-7 text-[#C5D1EB] text-[15px] leading-relaxed">
              An always-on gospel broadcast carrying worship, the Word,
              listener dedications, and prayer across the Bay of Plenty, the
              Waikato, and the world online — anointed for the Indian and
              multi-ethnic family of God.
            </p>

            <div className="mt-auto pt-8 flex flex-wrap items-center gap-4 text-sm text-[#C5D1EB]">
              <span className="flex items-center gap-2">
                <MapPin size={14} className="text-[#C5001A]" />
                Broadcasting from Tauranga, New Zealand
              </span>
            </div>
            <Link
              to="/listen"
              data-testid="radio-station-cta"
              className="mt-6 inline-flex items-center justify-center gap-2 border border-white/20 hover:bg-[#0B0C10] hover:border-[#C5001A] text-[#C5D1EB] hover:text-white px-5 py-3 text-sm transition-colors w-full sm:w-auto"
            >
              Open the studio <ArrowRight size={14} />
            </Link>
          </div>

          {/* Right: tune-in banner with red ambient glow */}
          <div className="lg:col-span-7 relative bg-white/[0.04] backdrop-blur-md border border-white/10 overflow-hidden flex flex-col justify-between p-8 sm:p-10">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-20 -top-20 w-80 h-80 rounded-full opacity-35 blur-[80px]"
              style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -left-12 -bottom-16 w-64 h-64 rounded-full opacity-25 blur-[80px]"
              style={{ background: "radial-gradient(circle, #E31C3D, transparent 70%)" }}
            />
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 text-[10px] tracking-[0.25em] uppercase backdrop-blur-md">
                <span className="live-dot" /> On Air · 24 / 7
              </span>
              <h3 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
                Tune in <span className="text-[#C5001A]">anywhere.</span>
                <br className="hidden sm:block" /> Listen live.
              </h3>
              <p className="mt-5 text-[#C5D1EB] text-base leading-relaxed max-w-md">
                FM across Tauranga, Te Puke, Whakatāne, Rotorua, Hamilton,
                Katikati, Pukekohe — and online from anywhere on the planet.
              </p>
            </div>

            <div className="relative mt-10 max-w-md">
              <Link
                to="/listen"
                data-testid="home-listen-live-cta"
                className="inline-flex w-full items-center justify-center gap-3 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-6 py-3.5 text-sm font-medium transition-colors shadow-[0_10px_30px_-10px_rgba(197,0,26,0.7)]"
              >
                <Play size={15} fill="currentColor" /> Listen Live
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function Home() {
  return (
    <main data-testid="home-page" className="bg-canvas">
      {/* Hero */}
      <section data-hero="home" className="relative overflow-hidden min-h-[560px] sm:min-h-[640px]">
        <HeroSlideshow />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-20 lg:pb-28">
          <div className="reveal flex items-center gap-3" style={{ animationDelay: "0.05s" }}>
            <span className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-white p-1.5 ring-1 ring-[#C5D1EB]/25">
              <img
                src={LOGO_LIFETALK_RADIO}
                alt="Life Talk Trust"
                className="w-full h-full object-contain"
              />
            </span>
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                Life Talk Trust · New Zealand
              </p>
              <p className="mt-1 font-display text-white text-sm tracking-tight">
                {TAGLINE}
              </p>
            </div>
          </div>

          <h1
            className="reveal mt-10 font-display font-black text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl"
            style={{ animationDelay: "0.15s" }}
          >
            Multi-ethnic gospel{" "}
            <span className="text-[#C5001A]">on-air & in person.</span>
            <br className="hidden sm:block" />
            From Tauranga to the nations.
          </h1>
          <p
            className="reveal mt-6 text-base sm:text-lg text-[#C5D1EB] max-w-2xl leading-relaxed"
            style={{ animationDelay: "0.25s" }}
          >
            A 24/7 gospel radio ministry across New Zealand and throughout
            the world, partnered with the Father's House Indian Church
            assembly in Tauranga — led by Pastors Robinson & Esther Martin.
          </p>

          <div
            className="reveal mt-10 flex flex-wrap items-center gap-3"
            style={{ animationDelay: "0.35s" }}
          >
            <Link
              to="/listen"
              data-testid="hero-listen-cta"
              className="inline-flex items-center gap-3 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-6 py-3.5 text-sm font-medium transition-colors"
            >
              <Play size={16} fill="currentColor" /> Listen Live
            </Link>
            <Link
              to="/church"
              data-testid="hero-church-cta"
              className="inline-flex items-center gap-2 border border-[#C5D1EB]/60 text-[#C5D1EB] hover:bg-[#1F2833] hover:text-white px-6 py-3.5 text-sm transition-colors"
            >
              Visit the Church <ArrowRight size={15} />
            </Link>
            <Link
              to="/giving"
              data-testid="hero-give-cta"
              className="inline-flex items-center gap-2 text-[#C5D1EB] hover:text-white px-3 py-3 text-sm transition-colors"
            >
              <Heart size={14} /> Give
            </Link>
          </div>

          {/* Inline live indicator strip */}
          <div className="reveal mt-12 flex items-center gap-4" style={{ animationDelay: "0.45s" }}>
            <span className="eq-bars">
              <span /><span /><span /><span />
            </span>
            <p className="text-xs tracking-[0.25em] uppercase text-[#C5D1EB]">
              Streaming now · Indian & multi-ethnic gospel
            </p>
          </div>
        </div>
      </section>

      {/* FM frequencies ticker */}
      <FmTicker />

      {/* Founders */}
      <FoundersBlock />

      {/* Trustees placeholders */}
      <TrusteesBlock />

      {/* Identity tabs */}
      <IdentityTabs />

      {/* Father's House block */}
      <FathersHouseBlock />

      {/* Life Talk Radio Station block — mirrors the Father's House box */}
      <RadioStationBlock />

      {/* Unity summary banner */}
      <section
        data-testid="unity-banner"
        className="relative overflow-hidden border-t border-white/5"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -left-10 -bottom-12 w-[36rem] h-[36rem] rounded-full opacity-25 blur-[120px]"
          style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <p className="text-white font-display text-lg sm:text-2xl leading-snug tracking-tight max-w-4xl">
            Together, <span className="text-[#C5001A]">Life Talk Radio New Zealand</span> and{" "}
            <span className="text-[#C5001A]">Father's House Indian Church Tauranga</span> exist to
            bring people closer to Jesus Christ, share His unfailing love, and see lives
            transformed by the power of the Gospel.
          </p>
        </div>
      </section>

      {/* Reviews — visitors share their experience */}
      <ReviewsBlock />
    </main>
  );
}
